<script setup lang="ts">
import { ImageOff, ImagePlus } from "@lucide/vue";
import { onMounted } from "vue";
import { useI18n } from "vue-i18n";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  FormAlert,
  Skeleton,
} from "@/shared/ui";

import { useMasterPortfolio } from "../model/useMasterPortfolio";
import MasterPortfolioTile from "./MasterPortfolioTile.vue";
import PortfolioUploadSheet from "./PortfolioUploadSheet.vue";

const props = defineProps<{ masterId: string }>();
const emit = defineEmits<{ notFound: [] }>();
const { t } = useI18n();

const {
  listStatus,
  photos,
  isFull,
  serviceOptions,
  resolveServiceName,
  reorderInFlight,
  politeMessage,
  upload,
  remove,
  load,
  openUpload,
  closeUpload,
  selectFile,
  setUploadService,
  confirmUpload,
  cancelUpload,
  requestRemove,
  cancelRemove,
  confirmRemove,
  moveBack,
  moveForward,
} = useMasterPortfolio(props.masterId, { onNotFound: () => emit("notFound") });

onMounted(load);
</script>

<template>
  <!-- sr-only polite live region -->
  <p
    role="status"
    aria-live="polite"
    aria-atomic="true"
    class="sr-only"
  >{{ politeMessage }}</p>

  <!-- Loading -->
  <div
    v-if="listStatus === 'loading'"
    aria-busy="true"
    class="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3"
  >
    <Skeleton
      v-for="i in 4"
      :key="i"
      class="aspect-square rounded-md"
    />
  </div>

  <!-- Error -->
  <div
    v-else-if="listStatus === 'error'"
    class="flex flex-col gap-3 p-4"
  >
    <FormAlert
      variant="destructive"
      :message="t('masters.portfolio.error')"
    />
    <Button
      variant="outline"
      size="sm"
      class="self-start"
      @click="load()"
    >
      {{ t('masters.portfolio.retry') }}
    </Button>
  </div>

  <!-- Ready -->
  <template v-else-if="listStatus === 'ready'">
    <!-- Empty state -->
    <div
      v-if="photos.length === 0"
      class="flex flex-col items-center gap-4 p-8 text-muted-foreground"
    >
      <ImageOff
        class="size-10"
        aria-hidden="true"
      />
      <p class="text-sm">{{ t('masters.portfolio.empty') }}</p>
      <Button
        variant="outline"
        size="sm"
        @click="openUpload()"
      >
        <ImagePlus aria-hidden="true" />
        {{ t('masters.portfolio.addPhoto') }}
      </Button>
    </div>

    <!-- Photo grid -->
    <div
      v-else
      class="flex flex-col gap-3 p-4"
    >
      <div
        role="list"
        class="grid grid-cols-2 gap-3 sm:grid-cols-3"
      >
        <MasterPortfolioTile
          v-for="photo in photos"
          :key="photo.id"
          :photo="photo"
          :service-name="resolveServiceName(photo.serviceId)"
          :is-first="photo.position === 0"
          :is-last="photo.position === photos.length - 1"
          :reorder-in-flight="reorderInFlight"
          @move-back="moveBack(photo.id)"
          @move-forward="moveForward(photo.id)"
          @delete="requestRemove(photo.id)"
        />

        <!-- Add tile — last cell in the grid, not a list item -->
        <div
          v-if="!isFull"
          class="aspect-square"
        >
          <button
            type="button"
            class="flex size-full items-center justify-center rounded-md border border-dashed border-border text-muted-foreground transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            :aria-label="t('masters.portfolio.a11y.addPhoto')"
            @click="openUpload()"
          >
            <ImagePlus
              class="size-6"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      <!-- Full hint -->
      <p
        v-if="isFull"
        class="text-xs text-muted-foreground"
      >
        {{ t('masters.portfolio.full') }}
      </p>
    </div>
  </template>

  <!-- Upload Sheet -->
  <PortfolioUploadSheet
    :open="upload.open"
    :status="upload.status"
    :progress="upload.progress"
    :preview-url="upload.previewUrl"
    :has-file="!!upload.file"
    :service-options="serviceOptions"
    :selected-service-id="upload.serviceId"
    :file-error="upload.fileErrorKey ? t(upload.fileErrorKey) : undefined"
    :service-error="upload.serviceErrorKey ? t(upload.serviceErrorKey) : undefined"
    @update:open="(v) => { if (!v) closeUpload() }"
    @select-file="selectFile"
    @update:selected-service-id="setUploadService"
    @submit="confirmUpload()"
    @cancel="cancelUpload()"
  />

  <!-- Delete AlertDialog -->
  <AlertDialog
    :open="remove.state !== 'idle'"
    @update:open="(v) => { if (!v && remove.state !== 'removing') cancelRemove() }"
  >
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ t('masters.portfolio.remove.title') }}</AlertDialogTitle>
        <AlertDialogDescription>{{ t('masters.portfolio.remove.description') }}</AlertDialogDescription>
      </AlertDialogHeader>

      <FormAlert
        v-if="remove.errorKey"
        variant="destructive"
        :message="t(remove.errorKey)"
      />

      <AlertDialogFooter>
        <AlertDialogCancel
          :disabled="remove.state === 'removing'"
          @click="cancelRemove()"
        >
          {{ t('masters.portfolio.remove.cancel') }}
        </AlertDialogCancel>
        <AlertDialogAction
          variant="destructive"
          :loading="remove.state === 'removing'"
          @click="confirmRemove()"
        >
          {{ t('masters.portfolio.remove.confirm') }}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
